(ns hai.tasks.assignee-picker
  "Popover-based searchable picker for reassigning a task, opened by clicking
   its assignee avatar. Searches the current user's personal contacts that have
   an email (`hai.tasks.data/::search-contacts` filters out the rest); the
   `task/assign` mutation then ensures (finds-or-creates, by email) the
   corresponding Humble user and assigns the task to them — reached either from
   a picked contact (`:contact/uuid`) or a typed address (`:user/email`).

   Modeled on `hai.notes.contact-combobox` / `hai.tags.combobox`: typing runs a
   debounced, server-side `:contact/search` query (`::search-contacts` stores
   the results in `::data/contacts`, shared by every row's picker on the page).
   The dropdown is empty until you type, since `:contact/search` returns nothing
   for a blank query.

   Picking a contact from the dropdown stages it as a removable pill; an explicit
   `Assign` button then applies it. Typing an address that isn't one of your
   contacts needs no staging: it enables `Assign` directly and shows an
   \"Add contact\" checkbox (on by default). Assign then applies either a contact
   (`:contact/uuid`) or a raw `{:user/email … :contact/add? <checked?>}`, both of
   which `task/assign` (and the task form) accept — `:contact/add?` tells the
   backend whether to also save the address to your personal contacts.

   Pass `:on-pick` to take over what Assign does — the task form uses it to
   stage the pick until Save instead of assigning on the spot."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as contacts]
   [hai.tasks.data :as data]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private person-icon (icons/mui-icon :outlined :person))
(def ^:private close-icon (icons/mui-icon :outlined :close))

;; Matches the sharing dropdown's typeahead (`searchable-multi-select`, 500ms).
;; The debounce alone doesn't prevent flicker — the no-flicker part is that the
;; rows aren't cleared per keystroke: `::data/search-contacts` stashes results in
;; a staging key and `::data/loaded-contacts` swaps them into `::data/contacts`
;; atomically, so the previous rows stay put until the new ones land.
(def ^:private debounce-ms 500)

(defn- valid-email?
  "Loose check — enough to decide whether a typed string can be offered as an
   assignable address. The backend is the authority on what it accepts."
  [s]
  (boolean (re-matches #"[^@\s]+@[^@\s]+\.[^@\s]+" (str/trim (or s "")))))

(defn- pill-label
  "Display text for a staged pill — a contact's name+email, or a raw address."
  [pill]
  (if (:contact/uuid pill)
    (contacts/->label+email pill)
    [:span (:user/email pill)]))

;; Unstyled-button reset around whatever avatar is passed in as `trigger` — only
;; adds a click affordance (pointer + hover/focus ring), the avatar itself keeps
;; its existing look. `.pill` is for wide triggers (the form's assignee chip),
;; where a 50% radius would draw the ring as an ellipse.
(o/defstyled picker-trigger :button
  :inline-flex :cursor-pointer
  {:border "none" :background "transparent" :padding "0" :border-radius "50%"
   :transition "box-shadow 0.15s ease"}
  [:&.pill {:border-radius "999px"}]
  [:&:hover {:box-shadow "0 0 0 2px var(t/--card-sage-soft-20)"}]
  [:&:focus-visible {:outline "none" :box-shadow "0 0 0 2px var(t/--card-sage)"}])

;; Top row: the search box (or a staged pill in its place) on the left, the
;; Assign button on the right. The left slot grows so Assign hugs the right edge;
;; `stretch` gives Assign the same 30px height as the box it sits beside.
(o/defstyled top-row :div
  :flex :items-stretch
  {:gap "6px" :margin-bottom "4px"})

;; Explicit `height` (with the existing `border-box`) so this and `pill-row`
;; share one deterministic row height — the staged pill *replaces* the input in
;; the same slot, and matching heights means neither picking nor clearing a
;; contact reflows anything. `flex: 1` makes it take the row's width left of
;; Assign.
(o/defstyled search-input :input
  {:flex "1" :min-width "0" :box-sizing "border-box"
   :height "30px"
   :padding "6px 10px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :border-radius "6px" :outline "none"
   :font-family "inherit" :font-size "0.8125rem"
   :color t/--card-forest}
  [:&:focus {:border-color t/--card-sage}]
  [:&::placeholder {:color t/--card-forest-70}])

;; Occupies the search box's exact slot (same height, same `flex: 1`) so swapping
;; the input out for a staged pill — and back, when the pill is cleared — doesn't
;; shift anything. The pill sits vertically centred, left-aligned, within it.
(o/defstyled pill-row :div
  :flex :items-center
  {:flex "1" :min-width "0" :height "30px"})

;; The results dropdown, floated just below the search row (or above it, via
;; `.flip-up`, when there isn't room below). Absolute (not a nested Radix
;; popover) on purpose: a Popover-inside-Popover reads the OUTER popover's anchor
;; and mispositions onto the search box. Being out of flow, it overlays the page
;; instead of growing/repositioning the picker popover.
(o/defstyled results-dropdown :div
  {:position "absolute" :top "calc(100% + 4px)" :left "0" :right "0"
   :z-index "50"
   :background t/--card-surface
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :border-radius "8px"
   :box-shadow "0 8px 24px rgba(0,0,0,0.14)"
   :overflow "hidden"}
  ;; Drop up: pin the bottom to the top of the search row instead.
  [:&.flip-up {:top "auto" :bottom "calc(100% + 4px)"}])

;; Separate styled component so its CSS still applies once it's rendered inside
;; `results-dropdown` — same trick as `contact-results`.
(o/defstyled results :div
  :flex :flex-col :overflow-y-auto
  {:max-height "220px"}
  [:.row :cursor-pointer :flex :items-center
   {:gap "8px" :padding "8px 10px" :font-size "0.8125rem" :border-radius "6px"}
   [:&:hover {:background t/--card-forest-06}]]
  [:.row-icon {:display "inline-flex" :color t/--card-forest-70}
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]]
  [:.empty {:padding "8px 10px" :font-size "0.8125rem" :color t/--card-forest-70}]
  ([{:keys [entries empty-label]}]
   (cond
     (seq entries)
     (into [:<>]
           (map (fn [{:keys [key label pick icon]}]
                  ^{:key key}
                  ;; `on-mouse-down` + `preventDefault` (not `on-click`) so the
                  ;; pick fires before the search input blurs — the list is a
                  ;; portalled popover layer, so a blur/dismiss race would
                  ;; otherwise swallow the click.
                  [:div.row {:on-mouse-down (fn [^js e] (.preventDefault e) (pick e))}
                   [:span.row-icon [(or icon person-icon)]]
                   [:span label]]))
           entries)
     ;; No `empty-label` (blank search) → render nothing rather than a
     ;; misleading "no matches" for a box the user hasn't typed into yet.
     empty-label [:div.empty empty-label])))

;; The staged selection, shown in the search box's slot (see `pill-row`) in place
;; of the input. A task has exactly one assignee, so a new pick replaces this
;; rather than adding to it; the ✕ clears it back to "nothing staged" (which
;; brings the input back). `pill-row` owns the vertical spacing, so the pill
;; itself carries none.
(o/defstyled staged-pill :div
  :inline-flex :items-center
  {:gap "6px"
   :padding "4px 6px 4px 10px"
   :border-width "1px" :border-style "solid" :border-color t/--card-sage-light
   :border-radius "999px"
   :background t/--property-3
   :font-size "0.8125rem" :color t/--card-forest :line-height "1"}
  [:.remove :inline-flex :cursor-pointer
   {:border "none" :background "transparent" :padding "2px"
    :border-radius "50%" :color t/--card-forest-70}
   [:&:hover {:background t/--card-forest-06 :color t/--card-forest}]
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  ([{:keys [pill on-remove]}]
   [:<>
    [:span (pill-label pill)]
    [:button.remove {:type "button" :aria-label "Remove" :on-click on-remove}
     [close-icon]]]))

;; Shown in the pill's slot when a not-yet-a-contact email is typed (the two are
;; mutually exclusive: staging a contact clears the search, so `trimmed` is empty
;; and no email is pending). On by default; unchecking still lets Assign through,
;; it just tells the backend not to save the address to the user's contacts.
(o/defstyled add-contact-toggle :label
  :inline-flex :items-center :cursor-pointer
  {:gap "6px" :margin "4px 0" :align-self "flex-start"
   :font-size "0.8125rem" :color t/--card-forest}
  ([{:keys [checked? on-change]}]
   [:<>
    [c/Checkbox {:checked         checked?
                 :onCheckedChange (fn [v] (on-change (boolean v)))}]
    [:span "Add contact"]]))

;; Sits at the right end of `top-row`, matching the search box's height via the
;; row's `stretch`. `flex: none` + `white-space: nowrap` keep it to its label's
;; width so the search box takes the rest.
(o/defstyled assign-button :button
  :inline-flex :items-center :justify-center
  {:flex "none"
   :padding "0 12px"
   :white-space "nowrap"
   :border "none" :border-radius "6px"
   :background t/--card-forest :color "white"
   :font-family "inherit" :font-size "0.8125rem" :font-weight "600"
   :cursor "pointer"
   :transition "opacity 0.15s ease"}
  [:&:hover {:opacity "0.9"}]
  [:&:disabled {:opacity "0.4" :cursor "not-allowed"}])

(o/defstyled popover-body :div
  :flex :flex-col
  ;; Wide enough that the search box stays roomy after `top-row` gives up space
  ;; to the Assign button — and applied here (not just on `results`) so the width
  ;; holds even when the results list is empty (pill staged / valid email typed).
  ;;
  ;; The floor yields to the viewport on the narrowest phones: 320px here plus
  ;; the popover's own 16px sides is 352px, wider than a 320px screen, and the
  ;; overflow falls on the right — where Assign sits, leaving a staged pill with
  ;; no way to apply it. `top-row` gives the shrink to the search box, which
  ;; carries `min-width: 0`.
  {:padding "8px" :min-width "min(320px, calc(100vw - 48px))"}
  ([{:keys [on-pick !open? !text !pill !add-contact? !anchor-el on-search]}]
   (let [contacts (or @(<s [::data/contacts]) [])
         query    @(<s [::data/contacts-query])
         text     @!text
         trimmed  (str/trim text)
         pill     @!pill
         add?     @!add-contact?
         stage!   (fn [selection]
                    ;; A task has one assignee — staging replaces any prior pill.
                    ;; Clear the search so the dropdown collapses onto the pill.
                    (reset! !pill selection)
                    (reset! !text "")
                    (>e [::data/clear-contacts]))
         contact-entries (mapv (fn [contact]
                                 {:key   (:contact/uuid contact)
                                  :label (contacts/->label+email contact)
                                  :pick  (fn [_] (stage! contact))})
                               contacts)
         ;; A valid address that isn't already one of the listed contacts' emails
         ;; is assignable on its own — no staging needed, so it isn't offered as a
         ;; dropdown row: it drives the checkbox + Assign below instead.
         new-email? (and (valid-email? trimmed)
                         (not (some (fn [c] (some #{trimmed} (:contact/email c)))
                                    contacts)))
         ;; What Assign will apply: a staged contact pill, or — when there's no
         ;; pill — the typed address, tagged with whether to also save it as a
         ;; contact. `nil` when there's nothing to assign (Assign stays disabled).
         selection  (cond
                      pill       pill
                      new-email? {:user/email trimmed :contact/add? add?})
         ;; Shown once a search has actually completed with no matching contacts.
         ;; The gate is "`query` is non-blank" — i.e. at least one search has come
         ;; back — NOT "`query` equals `text`". That distinction matters both ways:
         ;;  • non-blank `query` only becomes true after the first result lands, so
         ;;    we don't flash "No matches" on the very first keystroke before the
         ;;    server has answered;
         ;;  • it stays true across later keystrokes (unlike `= query text`, which
         ;;    breaks on every keystroke), so the message doesn't blink off mid-type
         ;;    and back on at each pause.
         ;; `contact-entries` holds the last completed search (not cleared per
         ;; keystroke), so it and this message stay in sync. A complete, valid email
         ;; is the one case with no message — the checkbox + Assign cover it.
         empty-label (when (and (not (str/blank? query))
                                (not (valid-email? trimmed)))
                       "No matches. Typing an email? Keep going")
         ;; The results are a floating dropdown (see below), so they open only when
         ;; there's something to show — otherwise the popover would anchor an empty
         ;; layer under the box.
         results-open? (boolean (or (seq contact-entries) empty-label))
         ;; Drop up when the box is close enough to the bottom of the viewport that
         ;; a downward list would run off-screen (and there's more room above).
         ;; Read live from the box's rect each render — no library, matches the
         ;; codebase's other absolute dropdowns, just adds the flip.
         flip-up? (when (and results-open? @!anchor-el)
                    (let [rect  (.getBoundingClientRect @!anchor-el)
                          below (- (.-innerHeight js/window) (.-bottom rect))
                          above (.-top rect)]
                      (and (< below 260) (> above below))))
         assign!  (fn []
                    (when selection
                      (on-pick selection)
                      (reset! !open? false)
                      (reset! !pill nil)
                      (reset! !text "")
                      (reset! !add-contact? true)))]
     [:<>
      ;; The search row plus its floating results dropdown. The raw `div` is the
      ;; positioning context (`position: relative`) and carries a ref so we can
      ;; measure the box to decide flip direction — an Ornament component wouldn't
      ;; forward the ref to its DOM node. The dropdown is absolutely positioned
      ;; against it, so it overlays the page rather than growing/repositioning this
      ;; popover. (A nested Radix popover can't be used here — inside the picker's
      ;; own popover it reads the OUTER anchor and lands on the search box.)
      [:div {:style {:position "relative"}
             :ref   (fn [el] (reset! !anchor-el el))}
       ;; The search box and a staged pill share one slot: pick a contact and the
       ;; pill takes the input's place (same height, no reflow); clear it and the
       ;; input — auto-focused again — comes back. The typed-email path never
       ;; stages a pill, so the box stays put there while you type the address.
       ;; Assign sits to the right of that slot.
       [top-row
        (if pill
          [pill-row [staged-pill {:pill pill :on-remove (fn [_] (reset! !pill nil))}]]
          [search-input
           {:type        "text"
            :value       text
            :placeholder "Search contacts or type an email…"
            :aria-label  "Search contacts or type an email"
            :auto-focus  true
            :on-change   (fn [^js e]
                           (let [v (.. e -target -value)]
                             (reset! !text v)
                             (on-search v)))}])
        [assign-button {:type     "button"
                        :disabled (nil? selection)
                        :on-click (fn [_] (assign!))}
         "Assign"]]
       (when results-open?
         [results-dropdown {:class (when flip-up? "flip-up")}
          [results {:entries contact-entries :empty-label empty-label}]])]
      (when new-email?
        [add-contact-toggle {:checked?  add?
                             :on-change (fn [v] (reset! !add-contact? v))}])])))

(defn component
  "`trigger` is the already-rendered avatar hiccup (e.g. `[assignee-avatar
   assignee]`). When `editable?` is false it's rendered as-is, unwrapped — a
   read-only row gets the plain, non-interactive avatar it always had.

   Assigning applies `task-uuid` straight away. `:on-pick` overrides that and
   receives the staged pick instead (a contact map, or `{:user/email …}`),
   leaving `task-uuid` unused — that's the mode the task form uses, since the
   form applies its assignee on Save rather than here. `:trigger-class` and
   `:label` style/name the trigger button."
  [{:keys [task-uuid editable? trigger trigger-class label on-pick]}]
  (if-not editable?
    trigger
    (r/with-let [!open?         (r/atom false)
                 !text          (r/atom "")
                 !pill          (r/atom nil)
                 !add-contact?  (r/atom true)
                 ;; Plain atom — a ref target for measuring the box to decide the
                 ;; dropdown's flip direction; it need not trigger re-renders.
                 !anchor-el     (atom nil)
                 !timer         (atom nil)
                 search!   (fn [text]
                             (if (str/blank? text)
                               (>e [::data/clear-contacts])
                               (>e [::data/search-contacts text])))
                 schedule! (fn [text]
                             (when-let [t @!timer] (js/clearTimeout t))
                             (reset! !timer (js/setTimeout #(search! text) debounce-ms)))]
      ;; Read from props on every render rather than freezing them in the
      ;; `with-let` bindings above, which only run at mount.
      (let [pick! (or on-pick
                      (fn [assignee]
                        (>e [::data/assign task-uuid assignee])))]
        [c/PopoverRoot
         {:open         @!open?
          :onOpenChange (fn [open?]
                          (reset! !open? open?)
                          (when-not open?
                            (reset! !text "")
                            (reset! !pill nil)
                            (reset! !add-contact? true)
                            (>e [::data/clear-contacts])))}
         [c/PopoverTrigger
          [:div [picker-trigger {:type       "button"
                                 :class      trigger-class
                                 :aria-label (or label "Change assignee")}
                 trigger]]]
         [c/PopoverContent {:side "bottom" :align "start" :sideOffset 4
                            ;; The results dropdown is an absolutely-positioned
                            ;; child that overflows this box (see `popover-body`);
                            ;; `visible` keeps it from being clipped here.
                            :style {:overflow "visible"}}
          [popover-body {:on-pick pick! :!open? !open? :!text !text :!pill !pill
                         :!add-contact? !add-contact? :!anchor-el !anchor-el
                         :on-search schedule!}]]])
      (finally
        (when-let [t @!timer] (js/clearTimeout t))))))
